import { useState } from "react";
import { Button } from "@/components/ui/button";
import { fmtDate, fmtTons } from "@/lib/format";
import { STATUS } from "./imageUtils";
import { Pencil, PlusCircle, Globe } from "lucide-react";

const FILTERS = ["all", "mismatch", "needs_check", "missing_on_website", "missing_in_book", "matched"];

const Summary = ({ slug, s }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 mb-3">
    {Object.keys(STATUS).map((k) => (
      <div key={k} data-testid={`summary-${slug}-${k}`} className={`rounded-lg border px-3 py-2 ${STATUS[k].cls}`}>
        <div className="text-[11px] uppercase tracking-wide">{STATUS[k].label}</div><div className="text-xl font-black">{s[k]}</div>
      </div>
    ))}
    <div className="rounded-lg border bg-white px-3 py-2"><div className="text-[11px] uppercase text-slate-500">Book Tons</div><div data-testid={`summary-${slug}-book-tons`} className="text-xl font-black">{fmtTons(s.book_tons)}</div></div>
    <div className="rounded-lg border bg-white px-3 py-2"><div className="text-[11px] uppercase text-slate-500">Website Tons</div><div data-testid={`summary-${slug}-web-tons`} className="text-xl font-black">{fmtTons(s.web_tons)}</div></div>
  </div>
);

const Row = ({ r, readOnly, onEditBook, onAddBook, onEditWeb }) => (
  <tr data-testid={`result-row-${r.key}`} className="border-b border-slate-100 align-top">
    <td className="p-2 whitespace-nowrap">{r.date ? fmtDate(r.date) : "?"}</td>
    <td className="p-2 font-medium">{r.customer}</td>
    <td className="p-2 text-right tabular-nums">{r.book_tons == null ? "—" : fmtTons(r.book_tons)}
      {r.book_text && r.book_text.includes("+") && <div className="text-[10px] text-slate-500">{r.book_text}</div>}</td>
    <td className="p-2 text-right tabular-nums">{r.web_tons == null ? "—" : fmtTons(r.web_tons)}</td>
    <td className={`p-2 text-right tabular-nums font-semibold ${r.diff ? "text-red-600" : "text-slate-400"}`}>{r.diff == null ? "—" : (r.diff > 0 ? "+" : "") + r.diff.toFixed(3)}</td>
    <td className="p-2">
      <span className={`inline-block text-xs border rounded px-2 py-0.5 ${STATUS[r.status].cls}`}>{STATUS[r.status].label}</span>
      {r.reasons.map((x) => <div key={x} className="text-[11px] text-slate-500 mt-0.5">{x}</div>)}
    </td>
    {!readOnly && (
      <td className="p-2 space-y-1 min-w-44">
        {r.book_entry_ids.length > 0 && r.status !== "matched" && (
          <Button size="sm" variant="outline" data-testid={`edit-book-${r.key}`} className="h-7 w-full" onClick={() => onEditBook(r)}><Pencil className="h-3 w-3 mr-1" />Correct AI reading</Button>
        )}
        {r.status === "missing_in_book" && (
          <Button size="sm" variant="outline" data-testid={`add-book-${r.key}`} className="h-7 w-full" onClick={() => onAddBook(r)}><PlusCircle className="h-3 w-3 mr-1" />Add missed book line</Button>
        )}
        {r.status !== "matched" && r.web_sales.map((s) => (
          <Button key={s.id} size="sm" variant="ghost" data-testid={`edit-web-${s.id}`} className="h-7 w-full text-red-700 hover:bg-red-50" onClick={() => onEditWeb(s)}>
            <Globe className="h-3 w-3 mr-1" />Edit website: {fmtTons(s.tons)}t {s.vehicle_no}
          </Button>
        ))}
      </td>
    )}
  </tr>
);

export const PlantResults = ({ plant, data, readOnly, ...handlers }) => {
  const [filter, setFilter] = useState("all");
  const slug = plant.replace(/\s+/g, "-").toLowerCase();
  const rows = data.rows.filter((r) => filter === "all" || r.status === filter);
  return (
    <section data-testid={`results-${slug}`} className="bg-white border rounded-xl p-4">
      <h3 className="font-chivo font-black text-xl mb-3">{plant}</h3>
      <Summary slug={slug} s={data.summary} />
      <div className="flex flex-wrap gap-1 mb-2">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`filter-${slug}-${f}`} onClick={() => setFilter(f)}
            className={`text-xs px-2.5 py-1 rounded-full border ${filter === f ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>
            {f === "all" ? "All" : STATUS[f].label}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500 border-b">
            <th className="p-2">Date</th><th className="p-2">Customer</th><th className="p-2 text-right">Book</th><th className="p-2 text-right">Website</th>
            <th className="p-2 text-right">Diff</th><th className="p-2">Status</th>{!readOnly && <th className="p-2">Actions</th>}
          </tr></thead>
          <tbody>{rows.map((r) => <Row key={r.key} r={r} readOnly={readOnly} {...handlers} />)}</tbody>
        </table>
        {rows.length === 0 && <p className="text-sm text-slate-400 p-3">No rows.</p>}
      </div>
    </section>
  );
};
